import { useLiveQuery, useServices } from "@hq/client-react";
import type { PaneComponentProps } from "@hq/layout";
import { Button, EmptyState, toast } from "@z/ui";
import { RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";

type TrashKind = "notes" | "tasks" | "folders" | "projects";
type TrashItem = { id: string; kind: TrashKind; title: string; deletedAt: string };
const labels = { notes: "Note", tasks: "Task", folders: "Folder", projects: "Project" };

/** Recovery is composed here so feature packages remain independent. */
export function TrashPane(_props: PaneComponentProps) {
  const services = useServices();
  const [restoring, setRestoring] = useState<string | null>(null);
  const { data: items, isLoading } = useLiveQuery(
    ["notes", "tasks", "folders", "projects"],
    async (store): Promise<TrashItem[]> => {
      const groups = await Promise.all(
        (["notes", "tasks", "folders", "projects"] as const).map(async (kind) =>
          (await store[kind].trashed()).map((item) => ({
            id: item.id,
            kind,
            title: ("title" in item ? item.title : item.name) || "Untitled",
            deletedAt: item.deletedAt!,
          })),
        ),
      );
      return groups.flat().sort((a, b) => b.deletedAt.localeCompare(a.deletedAt));
    },
    [],
  );
  const restore = async (item: TrashItem) => {
    setRestoring(item.id);
    try {
      await services[item.kind].restore(item.id);
      toast({ title: `Restored "${item.title}"` });
    } catch {
      toast({ title: `Could not restore ${labels[item.kind].toLowerCase()}`, variant: "danger" });
    } finally {
      setRestoring(null);
    }
  };
  return (
    <div className="mx-auto h-full max-w-3xl overflow-y-auto px-6 py-7">
      <h1 className="text-xl font-semibold tracking-tight">Trash</h1>
      <p className="mt-2 mb-6 text-sm text-fg-muted">
        Restore deleted notes, tasks, folders, and projects.
      </p>
      {isLoading ? (
        <p className="text-sm text-fg-muted">Loading deleted items…</p>
      ) : !items?.length ? (
        <EmptyState
          icon={<Trash2 />}
          title="Trash is empty"
          description="Deleted items will appear here so you can restore them."
        />
      ) : (
        <ul aria-label="Deleted items" className="space-y-1">
          {items.map((item) => (
            <li
              key={`${item.kind}:${item.id}`}
              className="flex items-center gap-3 rounded-lg px-3 py-3 hover:bg-fg/6"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{item.title}</p>
                <p className="text-xs text-fg-muted">{labels[item.kind]}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={restoring !== null}
                aria-label={`Restore ${labels[item.kind].toLowerCase()} ${item.title}`}
                onClick={() => void restore(item)}
              >
                <RotateCcw />
                {restoring === item.id ? "Restoring…" : "Restore"}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
